import React, { useState } from 'react';
import { X, User, Mail, Phone, GraduationCap, BookOpen, Clock, MessageSquare, Loader2, CheckCircle, AlertCircle, School } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const DemoBookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    phone: '',
    student_name: '',
    student_grade: '',
    curriculum: '',
    subjects: '',
    preferred_time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const grades = [
    'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 
    'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];

  const curriculumOptions = [
    'CBSE',
    'ICSE',
    'State Board',
    'IB (International Baccalaureate)',
    'IGCSE',
    'Other'
  ];

  const subjectOptions = [
    'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology',
    'English', 'Hindi', 'Social Studies', 'Computer Science', 'Other'
  ];

  const timeSlots = [
    'Morning (9 AM - 12 PM)',
    'Afternoon (12 PM - 4 PM)',
    'Evening (4 PM - 7 PM)',
    'Night (7 PM - 10 PM)',
    'Flexible'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject) => {
    const currentSubjects = formData.subjects ? formData.subjects.split(', ') : [];
    let newSubjects;
    
    if (currentSubjects.includes(subject)) {
      newSubjects = currentSubjects.filter(s => s !== subject);
    } else {
      newSubjects = [...currentSubjects, subject];
    }
    
    setFormData(prev => ({ ...prev, subjects: newSubjects.join(', ') }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            parent_name: '',
            email: '',
            phone: '',
            student_name: '',
            student_grade: '',
            curriculum: '',
            subjects: '',
            preferred_time: '',
            message: ''
          });
          setSubmitStatus(null);
          onClose();
        }, 3000);
      } else {
        const error = await response.json();
        setErrorMessage(error.detail || 'Failed to submit booking');
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting demo booking:', error);
      setErrorMessage('Network error. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center p-4 py-12">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl relative animate-in fade-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-5 py-3 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Book a Free Session</h2>
                <p className="text-cyan-100 text-xs">Experience personalized 1-on-1 tutoring</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Success State */}
          {submitStatus === 'success' && (
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600 text-sm">
                Your demo booking request has been submitted. 
                Our team will contact you shortly.
              </p>
            </div>
          )}

          {/* Error State */}
          {submitStatus === 'error' && (
            <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium text-sm">Submission Failed</p>
                <p className="text-red-600 text-xs">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Form */}
          {submitStatus !== 'success' && (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {/* Parent/Guardian Info */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <User className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Parent/Guardian Name *
                  </label>
                  <Input
                    name="parent_name"
                    value={formData.parent_name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="w-full h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Mail className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full h-9 text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Phone className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    className="w-full h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <User className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Student Name *
                  </label>
                  <Input
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleChange}
                    placeholder="Student's name"
                    required
                    className="w-full h-9 text-sm"
                  />
                </div>
              </div>

              {/* Student Grade */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <GraduationCap className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Grade/Class *
                  </label>
                  <select
                    name="student_grade"
                    value={formData.student_grade}
                    onChange={handleChange}
                    required
                    className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <School className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Curriculum/Board *
                  </label>
                  <select
                    name="curriculum"
                    value={formData.curriculum}
                    onChange={handleChange}
                    required
                    className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Select Curriculum</option>
                    {curriculumOptions.map(curr => (
                      <option key={curr} value={curr}>{curr}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subjects */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <BookOpen className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                  Subjects Interested In *
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {subjectOptions.map(subject => {
                    const isSelected = formData.subjects.includes(subject);
                    return (
                      <button
                        key={subject}
                        type="button"
                        onClick={() => handleSubjectToggle(subject)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-cyan-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {subject}
                      </button>
                    );
                  })}
                </div>
                {!formData.subjects && (
                  <p className="text-xs text-gray-500 mt-0.5">Select at least one subject</p>
                )}
              </div>

              {/* Preferred Time and Message in one row */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Clock className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Preferred Demo Time
                  </label>
                  <select
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <MessageSquare className="w-3.5 h-3.5 inline mr-1 text-cyan-600" />
                    Message (Optional)
                  </label>
                  <Input
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Any specific requirements?"
                    className="w-full h-9 text-sm"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.subjects}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-4 text-sm rounded-lg shadow-lg hover:shadow-xl transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Book Free Session'
                  )}
                </Button>
                <p className="text-center text-[10px] text-gray-500 mt-2">
                  By submitting, you agree to receive communications from FourQ Learn
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoBookingModal;
